import { useEffect } from "react";
import { RETURN_PARAM, readRememberedReturnUrl, rememberReturnUrl, sanitizeSameSiteReturnUrl } from "@/lib/returnUrl";

/**
 * AccountReturnGuard:
 * Ensures WooCommerce login and register forms contain a hidden input `redirect`
 * pointing to the safe same-origin return URL when coming from a gated page.
 *
 * Renders null. Safe against deferred DOM injection and re-renders.
 */
export default function AccountReturnGuard() {
  useEffect(() => {
    let targetUrl: string | null = null;
    try {
      if (typeof window !== "undefined") {
        const searchParams = new URLSearchParams(window.location.search);
        const rawRedirect = searchParams.get(RETURN_PARAM);
        if (rawRedirect) {
          const sanitized = sanitizeSameSiteReturnUrl(rawRedirect);
          if (sanitized) {
            targetUrl = sanitized;
            rememberReturnUrl(sanitized);
          }
        }
        if (!targetUrl) {
          targetUrl = readRememberedReturnUrl();
        }
      }
    } catch {
      targetUrl = null;
    }
    if (!targetUrl) {
      return;
    }
    let rafId: number | null = null;
    const applyRedirectInputs = () => {
      try {
        const formSelectors = "form.woocommerce-form-login, form.login, form.woocommerce-form-register, form.register";
        const forms = document.querySelectorAll<HTMLFormElement>(formSelectors);
        forms.forEach(form => {
          // Do not touch lost password forms if any selector matched a combined element
          if (form.classList.contains("lost_reset_password") || form.classList.contains("woocommerce-ResetPassword")) {
            return;
          }
          let redirectInput = form.querySelector<HTMLInputElement>('input[name="redirect"]');
          if (redirectInput) {
            if (redirectInput.value !== targetUrl) {
              redirectInput.value = targetUrl;
            }
          } else {
            redirectInput = document.createElement("input");
            redirectInput.type = "hidden";
            redirectInput.name = "redirect";
            redirectInput.value = targetUrl;
            redirectInput.setAttribute("data-tt-return", "1");
            form.appendChild(redirectInput);
          }
        });
      } catch {
        // Guard must never throw
      }
    };
    const scheduleApply = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        applyRedirectInputs();
      });
    };

    // Run immediately on mount
    scheduleApply();

    // Additional timeouts to catch deferred WooCommerce markup hydration
    const timer1 = setTimeout(scheduleApply, 300);
    const timer2 = setTimeout(scheduleApply, 1200);

    // MutationObserver to re-apply if WooCommerce redraws or injects forms
    const observer = new MutationObserver(mutations => {
      let hasChildListChange = false;
      for (const m of mutations) {
        if (m.type === "childList") {
          hasChildListChange = true;
          break;
        }
      }
      if (hasChildListChange) {
        scheduleApply();
      }
    });
    try {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    } catch {
      // ignore
    }
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      clearTimeout(timer1);
      clearTimeout(timer2);
      observer.disconnect();
    };
  }, []);
  return null;
}